import { syncListAriaHidden } from 'chairisher/util/accessibility';

/**
 * The selector used to choose the quantity field decrease trigger from the DOM
 */
const quantityFieldDecrementSelector = '.js-quantity-field-decrement';

/**
 * The selector used to choose the quantity field increase trigger from the DOM
 */
const quantityFieldIncrementSelector = '.js-quantity-field-increment';

/**
 * The selector used to choose the quantity field input element from the DOM
 */
const quantityFieldInputSelector = '.js-quantity-field-input';

/**
 * JS Component that can be used to change a quantity field
 *
 * @param {Object} settings
 * @param {jQuery} settings.$el The outermost element wrapping the quantity field
 * @param {Function=} settings.callback Optional function to run after the quantity changes
 * @param {number=} settings.val Optional value to initialize quantity picker with
 */
class QuantityField {
    constructor({ $el, callback = () => {}, val }) {
        this.$errorList = $el.find('.js-error-list');
        this.$decrementEl = $el.find(quantityFieldDecrementSelector);
        this.$el = $el;
        this.$incrementEl = $el.find(quantityFieldIncrementSelector);
        this.$quantityEl = $el.find(quantityFieldInputSelector);
        this.callback = () => callback(this.val);
        this.maxQuantity = parseInt(this.$quantityEl.attr('max'), 10) || Number.MAX_SAFE_INTEGER;
        this.minQuantity = parseInt(this.$quantityEl.attr('min'), 10) || 0;

        this.setVal(val !== undefined ? val : this.$quantityEl.val());
    }

    decrement() {
        this.setVal(this.val - 1);
        this.triggerChange();
    }

    /**
     * Increments the value of the quantity field
     */
    increment() {
        this.setVal(this.val + 1);
        this.triggerChange();
    }

    /**
     * @param {string|number} newVal The new value to set
     */
    setVal(newVal) {
        let parsedVal = parseInt(newVal, 10);

        if (Number.isNaN(parsedVal)) {
            parsedVal = this.val;
        } else if (newVal > this.maxQuantity) {
            parsedVal = this.maxQuantity;
        } else if (newVal < this.minQuantity) {
            parsedVal = this.minQuantity;
        }

        this.setError('');
        this.val = parsedVal;
        this.$quantityEl.val(this.val);
    }

    /**
     * Triggers change on the quantity element
     */
    triggerChange() {
        this.$quantityEl.trigger('change');
    }

    /**
     * Binds actions to the components that comprise a quantity field
     */
    bind() {
        this.$incrementEl.on('click', (e) => {
            e.preventDefault();
            this.increment();
            this.callback();
        });

        this.$decrementEl.on('click', (e) => {
            e.preventDefault();
            this.decrement();
            this.callback();
        });

        this.$quantityEl.on('blur', (e) => {
            e.preventDefault();
            this.setVal(e.target.value);
            this.callback();
        });

        this.$quantityEl.on('change', () => {
            this.$incrementEl.toggleClass('disabled', this.val === this.maxQuantity);
            this.$decrementEl.toggleClass('disabled', this.val === this.minQuantity);
        });

        this.$quantityEl.on('keyup', (e) => {
            const parsedVal = parseInt(e.target.value, 10);
            if (parsedVal > this.maxQuantity) {
                this.setError(`Only ${this.maxQuantity} available`);
            } else {
                this.setError('');
            }
        });
    }

    /**
     * @param {string} message
     */
    setError(message) {
        if (message) {
            this.$errorList.html(`<li>${message}</li>`);
        } else {
            this.$errorList.html('');
        }
        syncListAriaHidden(this.$errorList[0]);
    }
}

export default QuantityField;
